/**
 * Asset Counts export (AST-122).
 *
 * Turns the table the Asset Counts modal shows -- locationTable's output --
 * into CSV and Excel. Pure (apart from the xlsx writer), so EX-3..EX-9 are
 * tested without a browser.
 *
 * EX-3  exactly what the table shows: same groups, rows, columns, order.
 *       Built FROM the on-screen `table`, never recomputed, so the file can't
 *       drift from the screen.
 * EX-4  Group, Location, one column per asset, Total. Group is repeated on
 *       every row so the file sorts and filters cleanly.
 * EX-5  a final Total row
 * EX-6  plain numbers: no "+", no thousands separators in CSV, zeros as 0
 * EX-7  "<Project> Asset Counts <YYYY-MM-DD>.<ext>", local date
 * EX-8  CSV: UTF-8 with BOM, comma-delimited, header first, RFC 4180 quoting
 * EX-9  Excel: sheet "Asset Counts", header block (project + "Exported
 *       <date time> by <user>"), bold frozen table header, numbers as
 *       numbers, bold Total row and column
 */

export const SHEET_NAME = 'Asset Counts';
const BOM = '\uFEFF';

/**
 * Labels that differ between the two screens that export this shape.
 * Asset Counts (AST-122) is the default; Manual Asset Counts (AST-126)
 * passes { rowLabel: 'Project', totalLabel: 'Grand Total' }.
 */
const DEFAULT_LABELS = { rowLabel: 'Location', totalLabel: 'Total' };

/**
 * The export grid: header + one row per on-screen data row + Total row.
 * Every count is a plain integer. Returns { header, rows, totals } where
 * rows are [group, location, ...counts, total].
 */
export function exportRows(table, labels = DEFAULT_LABELS) {
  const { rowLabel, totalLabel } = { ...DEFAULT_LABELS, ...labels };
  const header = ['Group', rowLabel, ...table.columns.map((c) => c.name), 'Total'];
  const rows = [];
  for (const g of table.groups) {
    for (const r of g.rows) rows.push([g.label, r.name, ...r.cells, r.total]);
  }
  // Same label the modal uses for the project-level section -- but only when
  // there ARE mapped groups; with none, the table is just that one row and
  // the modal shows no section header either.
  if (table.project) {
    rows.push([table.groups.length ? 'Not at a mapped location' : '', table.project.name, ...table.project.cells, table.project.total]);
  }
  const totals = [totalLabel, '', ...table.grandTotal.cells, table.grandTotal.total];
  return { header, rows, totals };
}

/** RFC 4180: quote if it contains a comma, quote or newline; double quotes. */
function csvField(v) {
  if (typeof v === 'number') return String(v); // EX-6: no separators, 0 stays 0
  const s = v == null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Neutralise spreadsheet formula injection: a manual asset or location named
 * "=HYPERLINK(...)" would otherwise execute when the file is opened. Only
 * text cells are touched; counts are numbers and pass straight through.
 */
export function safeText(v) {
  const s = v == null ? '' : String(v);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}

/** EX-8 */
export function toCsv(table, labels) {
  const { header, rows, totals } = exportRows(table, labels);
  const line = (cells) => cells.map((c) => csvField(typeof c === 'number' ? c : safeText(c))).join(',');
  return BOM + [header, ...rows, totals].map(line).join('\r\n') + '\r\n';
}

const pad = (n) => String(n).padStart(2, '0');
/** YYYY-MM-DD in the user's own time zone (EX-7), not UTC. */
export const localDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localDateTime = (d) => `${localDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** EX-7. Characters no OS allows in a file name -- and control characters -- become "-". */
export function exportFileName(projectName, ext, now = new Date(), title = 'Asset Counts') {
  const safe = Array.from(String(projectName || 'Project'))
    .map((ch) => (ch.charCodeAt(0) < 32 || '\\/:*?"<>|'.includes(ch) ? '-' : ch))
    .join('')
    .trim() || 'Project';
  return `${safe} ${title} ${localDate(now)}.${ext}`;
}

/**
 * EX-9 sheet data for write-excel-file: rows of cell objects.
 * Layout: project name, "Exported ... by ...", blank, table header, rows,
 * Total. Returns { data, stickyRowsCount, columns }.
 */
export function toXlsxSheet(table, { projectName, user, now = new Date(), labels } = {}) {
  const { header, rows, totals } = exportRows(table, labels);
  const last = header.length - 1; // the Total column
  const cell = (v, i, bold) => (typeof v === 'number'
    ? { type: Number, value: v, ...(bold ? { fontWeight: 'bold' } : {}) }
    : { type: String, value: safeText(v), ...(bold ? { fontWeight: 'bold' } : {}) });

  const data = [
    [{ type: String, value: safeText(projectName || ''), fontWeight: 'bold' }],
    [{ type: String, value: safeText(`Exported ${localDateTime(now)} by ${user || 'unknown user'}`) }],
    [],
    header.map((h) => ({ type: String, value: safeText(h), fontWeight: 'bold' })),
    ...rows.map((r) => r.map((v, i) => cell(v, i, i === last))),
    totals.map((v) => cell(v, 0, true)),
  ];
  // Freeze through the table header so it stays visible while scrolling.
  const stickyRowsCount = 4;
  const columns = header.map((h, i) => ({ width: i < 2 ? Math.max(12, Math.min(32, h.length + 4)) : Math.max(10, h.length + 2) }));
  columns[0].width = 26; // "Not at a mapped location"
  columns[1].width = 28; // "Project level (no location)"
  return { data, stickyRowsCount, columns };
}

/** Browser-only: trigger a download of a Blob. */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick: some browsers cancel a download whose URL is
  // revoked synchronously.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
